import * as React from "react"
import { Calendar, ExternalLink, ImageIcon } from "lucide-react"
import { CardWidget, CardWidgetContent, CardWidgetDescription, CardWidgetHeader, CardWidgetTitle } from "./card-widget"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip"
import { cn } from "../../lib/utils"

export interface ContentEntry {
  // Core fields
  title: string
  description: string
  date: string
  link: string
  linkText?: string
  
  // Event-specific fields
  speaker?: string
  time?: string
  location?: string
  price?: string
  duration?: string
  category?: string
  organizer?: string
  capacity?: string
  status?: 'available' | 'sold-out' | 'cancelled'
  
  // Optional content
  image?: string
  details?: string
  extras?: string
}

interface GridWidgetProps {
  entries: ContentEntry[]
  columns?: 1 | 2 | 3 | 4
  className?: string
  showImages?: boolean
}

const GridWidget = React.forwardRef<HTMLDivElement, GridWidgetProps>(
  ({ entries, columns = 3, className, showImages = true, ...props }, ref) => {
    const gridCols = {
      1: "dsw-grid-cols-1",
      2: "dsw-grid-cols-1 md:dsw-grid-cols-1",
      3: "dsw-grid-cols-1 md:dsw-grid-cols-1 lg:dsw-grid-cols-2",
      4: "dsw-grid-cols-1 md:dsw-grid-cols-1 lg:dsw-grid-cols-2 xl:dsw-grid-cols-3"
    }

    // Helper function to validate and clean entry data
    const cleanEntry = (entry: ContentEntry) => {
      const hasValidLink = entry.link && entry.link.trim() && entry.link !== '#'
      const defaultLinkText = entry.speaker ? "Register" : "View"
      
      return {
        ...entry,
        hasValidLink,
        defaultLinkText
      }
    }

    return (
      <TooltipProvider>
        <div
          ref={ref}
          className={cn(
            "dsw-grid dsw-gap-4",
            gridCols[columns],
            className
          )}
          {...props}
        >
        {entries.map((entry, index) => {
          const cleanedEntry = cleanEntry(entry)
          return (
          <CardWidget key={index} className="dsw-h-full dsw-flex dsw-flex-col">
            {showImages && (
              <div className="dsw-aspect-video dsw-bg-muted dsw-flex dsw-items-center dsw-justify-center dsw-rounded-t-lg">
                {entry.image ? (
                  <img 
                    src={entry.image} 
                    alt={entry.title}
                    className="dsw-w-full dsw-h-full dsw-object-cover dsw-rounded-t-lg"
                  />
                ) : (
                  <ImageIcon className="dsw-h-12 dsw-w-12 dsw-text-muted-foreground" />
                )}
              </div>
            )}
            <CardWidgetHeader className="dsw-flex-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <CardWidgetTitle className="dsw-line-clamp-2 dsw-cursor-help">{cleanedEntry.title}</CardWidgetTitle>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="dsw-max-w-xs">{cleanedEntry.title}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <CardWidgetDescription className="dsw-line-clamp-3 dsw-cursor-help">
                    {cleanedEntry.description}
                  </CardWidgetDescription>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="dsw-max-w-xs">{cleanedEntry.description}</p>
                </TooltipContent>
              </Tooltip>
            </CardWidgetHeader>
            <CardWidgetContent className="dsw-pt-0">
                              {/* Event Details */}
                <div className="dsw-space-y-1 dsw-mb-3">
                  <p className="dsw-text-xs dsw-font-medium">
                    <strong>Speaker:</strong> {cleanedEntry.speaker || 'Speaker TBA'}
                  </p>
                  {cleanedEntry.location && (
                    <p className="dsw-text-xs dsw-text-muted-foreground">
                      <strong>Location:</strong> {cleanedEntry.location}
                    </p>
                  )}
                  {cleanedEntry.price && (
                    <p className="dsw-text-xs dsw-font-medium dsw-text-green-600">
                      {cleanedEntry.price}
                    </p>
                  )}
                  {cleanedEntry.status && cleanedEntry.status !== 'available' && (
                    <span className={cn(
                      "dsw-inline-block dsw-px-2 dsw-py-1 dsw-rounded dsw-text-xs dsw-font-medium",
                      cleanedEntry.status === 'sold-out' && "dsw-bg-red-100 dsw-text-red-800",
                      cleanedEntry.status === 'cancelled' && "dsw-bg-gray-100 dsw-text-gray-800"
                    )}>
                      {cleanedEntry.status === 'sold-out' ? 'Sold Out' : 'Cancelled'}
                    </span>
                  )}
                </div>
                
                {/* Date and Time Row */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="dsw-flex dsw-items-center dsw-gap-1 dsw-text-xs dsw-text-muted-foreground dsw-mb-3 dsw-cursor-help">
                      <Calendar className="dsw-h-3 dsw-w-3 dsw-flex-shrink-0" />
                      <span className="dsw-truncate">
                        {cleanedEntry.date}
                        <span className="dsw-ml-1">• {entry.time || 'Time TBA'}</span>
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="dsw-max-w-xs">
                      {cleanedEntry.date} {entry.time && `at ${entry.time}`}
                    </p>
                  </TooltipContent>
                </Tooltip>
                
                {/* Action Button Row */}
                <div className="dsw-flex dsw-justify-end">
                  {cleanedEntry.hasValidLink ? (
                    <a 
                      href={cleanedEntry.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="dsw-inline-flex dsw-items-center dsw-justify-center dsw-whitespace-nowrap dsw-text-xs dsw-font-medium dsw-ring-offset-background dsw-transition-colors dsw-focus-visible:dsw-outline-none dsw-focus-visible:dsw-ring-2 dsw-focus-visible:dsw-ring-ring dsw-focus-visible:dsw-ring-offset-2 dsw-disabled:dsw-pointer-events-none dsw-disabled:dsw-opacity-50 dsw-cursor-pointer dsw-border dsw-border-input dsw-bg-background hover:dsw-bg-accent hover:dsw-text-accent-foreground dsw-h-8 dsw-px-3 dsw-rounded-md"
                    >
                      <ExternalLink className="dsw-h-3 dsw-w-3 dsw-mr-1" />
                      {cleanedEntry.linkText || cleanedEntry.defaultLinkText}
                    </a>
                  ) : (
                    <span className="dsw-text-xs dsw-text-muted-foreground dsw-italic">
                      Contact organizer
                    </span>
                  )}
                </div>
            </CardWidgetContent>
          </CardWidget>
          )
        })}
        </div>
      </TooltipProvider>
    )
  }
)
GridWidget.displayName = "GridWidget"

export { GridWidget } 